import type {
  InitiateListingPaymentRequest,
  ListingPaymentIntentReadStatus,
  ListingPaymentRuntimeSession,
  ListingPaymentStatus,
  PersistedListingPaymentIntent,
  RecoverCurrentListingPaymentResponse,
} from "../types/listingPayment";

export const LISTING_PAYMENT_INTENT_VERSION = 2 as const;
export const LISTING_PAYMENT_INTENT_TTL_MS = 30 * 60 * 1000;
// Local intent timestamps can differ slightly from Date.now across an app
// transition, but a materially future-created intent is never trusted.
export const LISTING_PAYMENT_INTENT_MAX_FUTURE_SKEW_MS = 60 * 1000;
export const LISTING_PAYMENT_INTENT_STORAGE_PREFIX =
  "sokogrid:listing-payment:intent:v2";

const IDEMPOTENCY_KEY_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;
const OWNER_SCOPE_HASH_PATTERN = /^[a-f0-9]{64}$/;
const INTENT_KEYS = [
  "version",
  "ownerScopeHash",
  "idempotencyKey",
  "createdAtMs",
  "expiresAtMs",
] as const;

export type AuthoritativePaymentRecoveryState =
  | "checking"
  | "none"
  | "present"
  | "error";

export type ParsedListingPaymentIntent = {
  status: ListingPaymentIntentReadStatus;
  intent: PersistedListingPaymentIntent | null;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function hasExactIntentKeys(value: Record<string, unknown>) {
  const keys = Object.keys(value).sort();
  const expected = [...INTENT_KEYS].sort();

  return (
    keys.length === expected.length &&
    keys.every((key, index) => key === expected[index])
  );
}

export function isValidListingPaymentIdempotencyKey(
  value: unknown
): value is string {
  return typeof value === "string" && IDEMPOTENCY_KEY_PATTERN.test(value);
}

export function isValidOwnerScopeHash(value: unknown): value is string {
  return typeof value === "string" && OWNER_SCOPE_HASH_PATTERN.test(value);
}

export function listingPaymentIntentStorageKey(ownerScopeHash: string) {
  if (!isValidOwnerScopeHash(ownerScopeHash)) {
    throw new Error("Invalid listing payment owner scope.");
  }

  return `${LISTING_PAYMENT_INTENT_STORAGE_PREFIX}:${ownerScopeHash}`;
}

export function createListingPaymentIntent(args: {
  ownerScopeHash: string;
  idempotencyKey: string;
  nowMs: number;
}): PersistedListingPaymentIntent {
  if (
    !isValidOwnerScopeHash(args.ownerScopeHash) ||
    !isValidListingPaymentIdempotencyKey(args.idempotencyKey) ||
    !Number.isSafeInteger(args.nowMs) ||
    args.nowMs <= 0
  ) {
    throw new Error("Invalid listing payment intent.");
  }

  return {
    version: LISTING_PAYMENT_INTENT_VERSION,
    ownerScopeHash: args.ownerScopeHash,
    idempotencyKey: args.idempotencyKey,
    createdAtMs: args.nowMs,
    expiresAtMs: args.nowMs + LISTING_PAYMENT_INTENT_TTL_MS,
  };
}

export function parseListingPaymentIntent(args: {
  value: unknown;
  expectedOwnerScopeHash: string;
  nowMs: number;
}): ParsedListingPaymentIntent {
  const { value, expectedOwnerScopeHash, nowMs } = args;

  if (!isRecord(value) || !hasExactIntentKeys(value)) {
    return { status: "malformed", intent: null };
  }

  if (
    value.version !== LISTING_PAYMENT_INTENT_VERSION ||
    value.ownerScopeHash !== expectedOwnerScopeHash ||
    !isValidOwnerScopeHash(value.ownerScopeHash) ||
    !isValidListingPaymentIdempotencyKey(value.idempotencyKey) ||
    !Number.isSafeInteger(value.createdAtMs) ||
    !Number.isSafeInteger(value.expiresAtMs) ||
    (value.createdAtMs as number) <= 0 ||
    value.expiresAtMs !==
      (value.createdAtMs as number) + LISTING_PAYMENT_INTENT_TTL_MS ||
    !Number.isSafeInteger(nowMs) ||
    nowMs <= 0 ||
    (value.createdAtMs as number) >
      nowMs + LISTING_PAYMENT_INTENT_MAX_FUTURE_SKEW_MS
  ) {
    return { status: "malformed", intent: null };
  }

  const intent = value as PersistedListingPaymentIntent;

  if (nowMs >= intent.expiresAtMs) {
    return { status: "expired", intent: null };
  }

  return { status: "valid", intent };
}

export function canCreateNewListingPaymentKey(args: {
  recoveryState: AuthoritativePaymentRecoveryState;
  hasRuntimeSession: boolean;
}) {
  return args.recoveryState === "none" && !args.hasRuntimeSession;
}

export function shouldClearLegacyListingPaymentSession(
  authoritativeRecoverySucceeded: boolean
) {
  return authoritativeRecoverySucceeded;
}

export function legacyCleanupFailurePolicy(
  authoritativeStatus: RecoverCurrentListingPaymentResponse["status"]
) {
  return {
    blocksNewPayment: authoritativeStatus === "none",
    showPrivacyWarning: authoritativeStatus !== "none",
  };
}

export function runtimeSessionFromAuthoritativeRecovery(args: {
  uid: string;
  localIntent: PersistedListingPaymentIntent | null;
  recovery: RecoverCurrentListingPaymentResponse;
}): { session: ListingPaymentRuntimeSession | null; requiresReview: boolean } {
  const { uid, localIntent, recovery } = args;

  if (recovery.status === "none") {
    return {
      session: localIntent
        ? { uid, intent: localIntent, phoneNumber: "" }
        : null,
      requiresReview: false,
    };
  }

  if (recovery.status === "verification_required" && !recovery.payment) {
    return {
      session: {
        uid,
        intent: localIntent ?? undefined,
        lastStatus: "verification_required",
        manualReviewRequired: true,
      },
      requiresReview: false,
    };
  }

  const payment = recovery.payment;
  if (!payment) return { session: null, requiresReview: true };

  return {
    session: {
      uid,
      intent: localIntent ?? undefined,
      paymentId: payment.paymentId,
      lastStatus:
        recovery.status === "consumed" ? "succeeded" : recovery.status,
      entitlementId:
        recovery.status === "consumed"
          ? recovery.entitlementId
          : payment.entitlementId,
      businessId:
        recovery.status === "consumed" ? recovery.businessId : undefined,
      maskedPhone: payment.maskedPhone,
      manualReviewRequired:
        recovery.status === "verification_required" ? true : undefined,
    },
    requiresReview: false,
  };
}

export function canConfirmTerminalPaymentRetry(args: {
  authoritativeStatus: ListingPaymentStatus | "none" | "consumed";
  userConfirmed: boolean;
  phoneNumberIsValid: boolean;
}) {
  return (
    args.userConfirmed &&
    args.phoneNumberIsValid &&
    (args.authoritativeStatus === "failed" ||
      args.authoritativeStatus === "cancelled" ||
      args.authoritativeStatus === "timed_out")
  );
}

export function buildListingPaymentInitiationRequest(args: {
  intent: PersistedListingPaymentIntent;
  phoneNumber: string;
  configurationVersion: number;
  configurationFingerprint: string;
}): InitiateListingPaymentRequest {
  if (
    !isValidListingPaymentIdempotencyKey(args.intent.idempotencyKey) ||
    !args.phoneNumber.trim() ||
    !Number.isSafeInteger(args.configurationVersion) ||
    args.configurationVersion <= 0 ||
    !/^[a-f0-9]{64}$/.test(args.configurationFingerprint)
  ) {
    throw new Error("Invalid listing payment initiation request.");
  }

  return {
    phoneNumber: args.phoneNumber.trim(),
    idempotencyKey: args.intent.idempotencyKey,
    configurationVersion: args.configurationVersion,
    configurationFingerprint: args.configurationFingerprint,
  };
}

export type CompletionSingleFlight = {
  run: (operation: () => Promise<void>) => Promise<void>;
  reset: () => void;
};

/**
 * Shares the complete paid-draft/navigation/cleanup operation. Successful work
 * remains cached until the authenticated owner changes; failures unlock retry.
 */
export function createCompletionSingleFlight(): CompletionSingleFlight {
  let current: Promise<void> | null = null;

  return {
    run(operation) {
      if (current) return current;

      const request = Promise.resolve().then(operation);
      current = request;
      request.then(undefined, () => {
        if (current === request) current = null;
      });
      return request;
    },
    reset() {
      current = null;
    },
  };
}
